'use client';

import {
  AudioFile as AudioFileIcon,
  InsertDriveFile as InsertDriveFileIcon,
  VideoFile as VideoFileIcon,
} from '@mui/icons-material';
import {
  Box,
  Card,
  CircularProgress,
  IconButton,
  SvgIcon,
  Typography,
} from '@mui/material';
import { DownloadSimple, TrashSimple } from '@phosphor-icons/react';
import config from '../config';

const getIcon = (mimeType?: string) => {
  if (mimeType && config.ACCEPTED_MEDIA_TYPES.AUDIO_TYPE.includes(mimeType)) {
    return <AudioFileIcon />;
  }

  if (mimeType && config.ACCEPTED_MEDIA_TYPES.VIDEO_TYPE.includes(mimeType)) {
    return <VideoFileIcon />;
  }

  return <InsertDriveFileIcon />;
};

type Props = {
  onDelete?: () => void;
  downloadHref?: string | null;
  mimeType?: string;
  isLoading?: boolean;
  filename?: string;
  imagePreview?: string | null;
};

export default function FileCard({
  onDelete,
  downloadHref,
  mimeType,
  isLoading,
  filename,
  imagePreview,
}: Props) {
  if (isLoading) {
    return (
      <Card
        sx={{
          display: 'flex',
          alignItems: 'center',
          padding: 2,
          minWidth: 300,
          gap: 2,
        }}
      >
        <CircularProgress size={24} />
        <Typography variant="caption">Uploading...</Typography>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        display: 'flex',
        alignItems: 'center',
        padding: 1,
        gap: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 40,
          height: 40,
          backgroundColor: 'white',
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        {imagePreview ? (
          <img
            src={imagePreview}
            alt={filename || 'Preview'}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        ) : (
          <SvgIcon sx={{ color: 'text.secondary' }}>
            {getIcon(mimeType)}
          </SvgIcon>
        )}
      </Box>

      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" noWrap>
          {filename ||
            (mimeType === 'video/quicktime' ? 'video/mp4' : mimeType)}
        </Typography>
      </Box>

      {downloadHref && (
        <IconButton
          href={downloadHref}
          target="_blank"
          sx={{
            color: 'primary.main',
          }}
        >
          <SvgIcon>
            <DownloadSimple />
          </SvgIcon>
        </IconButton>
      )}

      {onDelete && (
        <IconButton
          onClick={onDelete}
          sx={{
            color: 'text.secondary',
          }}
        >
          <SvgIcon>
            <TrashSimple />
          </SvgIcon>
        </IconButton>
      )}
    </Card>
  );
}
